import { action, makeAutoObservable, makeObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { GeneratorAPI } from '~/services/api';
import store from '~/stores';
import { jsPDF } from 'jspdf';
import { alert } from '~/utils';
import { format } from 'date-fns';
import FileSaver from 'file-saver';

export default class TestCaseStore implements ItemStore {
  @observable
  issueType: 'Test Case' = 'Test Case';

  @observable
  selected?: TestCase.Type;

  @observable
  items: TestCase.Type[] = [];

  @observable
  generatedItems: GeneratedItem[] = [];

  @observable isExporting = false;

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'TestCaseStore',
      properties: ['selected', 'items'],
      storage: window.localStorage,
    });
    this.saveItemsLocalStorage();
  }

  @action
  select(id: number): void {
    this.selected = this.items.find(item => item.id === id);
  };

  @action
  add(item: TestCase.Type): void {
    this.items.push(item);
  };

  @action
  edit(item: TestCase.Type): void {
    const index = this.items.findIndex(existingItem => existingItem.id === item.id);
    this.items[index] = item;
  };

  @action
  remove(id: number): void {
    this.items = this.items.filter(item => item.id !== id);
    this.selected = undefined;
  };

  @action
  resetItems(): void {
    this.items = [];
  };

  @action
  setFeedback = async (id: number, feedback: number): Promise<void> => {
    const index = this.items.findIndex(item => item.id === id);
    this.items[index].feedback = feedback;
    if (this.generatedItems.length > 0) {
      const indexGeneratedItem = this.generatedItems.findIndex(item => item.id === id);
      if (indexGeneratedItem !== -1) {
        this.generatedItems[indexGeneratedItem].feedback = feedback;
      }
    }
    await this.onTestCaseFeedback(this.items[index]);
  };

  @action
  onTestCase = async (data: TestCase.Forms): Promise<any> => {
    const rsp = await GeneratorAPI.onTestCase(data);
    return rsp;
  };

  @action
  getTestCase = async (data: TestsType.GetResponse): Promise<boolean> => {
    this.generatedItems = [];
    const rsp = await GeneratorAPI.getCaseTest(data);
    if (rsp.length === 0) {
      return false;
    } else {
      rsp[0].content.map((item) => {
        let itemId = store.itemId.generate();
        const description = Array.isArray(item.description) ? item.description.join('\n') : item.description;
        this.add({
          id: itemId,
          artifact_id: rsp[0]._id,
          item_id: item.id,
          feedback: Number(item.feedback),
          content: {
            summary: item.summary,
            description: description,
          },
          project: store.project.selected!,
          forms: {
            system: '',
            epic: '',
            functionality: '',
            rules: '',
            userStory: '',
            acceptanceCriteria: '',
            denialCriteria: '',
            amount: rsp[0].content.length,
          },
          type: 'custom'
        });
        this.generatedItems.push({
          project: store.project.selected!,
          id: itemId,
          feedback: Number(item.feedback),
          content: {
            summary: item.summary,
            description: description,
          }
        });
      });
      return true;
    }
  };

  @action
  updateTestCase = async (testCase: TestCase.Type): Promise<any> => {
    const rsp = await GeneratorAPI.updateTestCase(testCase);
    return rsp;
  };

  @action
  onTestCaseFeedback = async (testCase: TestCase.Type): Promise<any> => {
    const rsp = await GeneratorAPI.onTestCaseFeedback(testCase);
    return rsp;
  };

  @action
  saveItemsLocalStorage(): void {
    const testCaseStore = localStorage.getItem('TestCaseStore');
    if (testCaseStore) {
      const testCaseStoreJson = JSON.parse(testCaseStore);
      testCaseStoreJson.items = this.items;
      localStorage.setItem('TestCaseStore', JSON.stringify(testCaseStoreJson));
    }
  };

  @action
  exportToPDF = (): void => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(12);

      const selectedProject = store.project.selected;
      const projectName = store.project.selected?.name || '';
      const projectId = selectedProject?.id;
      const date = new Date();
      const formattedDate = format(date, 'dd-MM-yyyy HH:mm:ss');

      const filteredItems = this.items.filter(item =>
        projectId ? item.project.id === projectId && item.feedback === 2 : false
      )

      if (filteredItems.length === 0) {
        doc.text("Nenhum caso de teste encontrado.", 10, 10);
      } else {
        filteredItems.forEach((item, index) => {
          const startY = 10 + (index * 30);

          const summaryLines = doc.splitTextToSize(`${item.content.summary}`, 190);
          doc.setFont('helvetica', 'bold');
          doc.text(summaryLines, 10, startY + 8);
          const descriptionLines = doc.splitTextToSize(`Descrição: ${item.content.description}`, 190);
          doc.setFont('helvetica', 'normal');
          doc.text(descriptionLines, 10, startY + 8 + summaryLines.length * 8);
        });
      }

      const pdfFileName = `${projectName}_${formattedDate.replace(/[:\s]/g, '-')}.pdf`;
      doc.save(pdfFileName);

      alert({
        message: 'Caso de teste exportado com sucesso!',
        type: 'success',
        position: 'top-center',
      });
    } catch (error) {
      alert({
        message: `Erro ao exportar caso de teste: ${(error as Error).message}`,
        type: 'error',
        position: 'top-center',
      });
    }
  };

  @action
  exportItemsToTxt(): void {
    const projectId = store.project.selected?.id;
    const content = this.items
      .filter(item => projectId ? item.project.id === projectId && item.feedback === 2 : false)
      .map(item => {
        if (item.content) {
          return `${item.content.summary}\nDescrição: ${item.content.description}`;
        } else {
          return "Conteúdo não disponível";
        }
      }).join('\n\n');

    const projectName = store.project.selected?.name || '';
    const date = new Date();
    const formattedDate = format(date, 'dd-MM-yyyy HH:mm:ss');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    try {
      FileSaver.saveAs(blob, `${projectName}_${formattedDate}.txt`);
      alert({
        message: 'Caso de teste exportado com sucesso!',
        type: 'success',
        position: 'top-center',
      });
    } catch (error) {
      alert({
        message: `Erro ao exportar caso de teste: ${(error as Error).message}`,
        type: 'error',
        position: 'top-center',
      });
    }
  };

  @action
  exportToAzureDevOps = async (): Promise<boolean> => {
    try {
      const projectId = store.project.selected?.id;
      const filteredItems = this.items.filter(item => projectId ? item.project.id === projectId && item.feedback === 2 : false);
      if (filteredItems.length === 0) {
        alert({
          message: 'Nenhum caso de teste selecionado para exportação.',
          type: 'warning',
          position: 'top-center',
        });
        return false;
      }
      const csv_data = filteredItems.map(item => ({
        id: item.id,
        summary: item.content.summary,
        description: Array.isArray(item.content.description) ? item.content.description : String(item.content.description).split('\n'),
      }));
      const response = await GeneratorAPI.exportItemsToAzureDevOpsCSV({ csv_data });
      if (response && response.status === 200 && response.data) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
        const projectName = store.project.selected?.name || 'export';
        FileSaver.saveAs(blob, `${projectName}-azure.csv`);
        alert({
          message: 'Casos de teste exportados para Azure DevOps com sucesso!',
          type: 'success',
          position: 'top-center',
        });
        return true;
      } else {
        alert({
          message: `Erro ao exportar casos de teste para Azure DevOps: ${response.statusText || response.error}`,
          type: 'error',
          position: 'top-center',
        });
        return false;
      }
    } catch (err: any) {
      alert({
        message: `Erro ao exportar casos de teste para CSV (Azure DevOps): ${err.message}`,
        type: 'error',
        position: 'top-center',
      });
      return false;
    }
  }

  @action
  exportToJira = async (): Promise<boolean> => {
    this.isExporting = true;
    const issues: Jira.ExportRequest[] = this.items
      .filter(item => item.feedback === 2)
      .map(item => ({
        project: String(store.project.selected!.id),
        summary: item.content.summary,
        description: item.content.description,
        issuetype: {
          hierarchyLevel: 0,
          name: "Test",
        },
      }));

    try {
      const response = await GeneratorAPI.exportItemsToJira({ issues });
      if (response.status === 201) {
        alert({
          message: 'Casos de teste exportados para o Jira com sucesso!',
          type: 'success',
          position: 'top-center',
        });
        return true;
      } else {
        alert({
          message: `Erro ao exportar casos de teste para o Jira: ${response.status} | ${response.statusText}`,
          type: 'error',
          position: 'top-center',
        });
        return false;
      }
    } finally {
      this.isExporting = false;
    }
  };

  @action
  exportToAzure = async (): Promise<boolean> => {
    this.isExporting = true;
    const workitems: Azure.ExportRequest = {
      project: String(store.project.selected!.id),
      work_items: this.items
        .filter(item => item.feedback === 2)
        .map(item => ({
          work_item_type: 'Test Case',
          title: item.content.summary,
          description: item.content.description
        }))
    }

    try {
      const response = await GeneratorAPI.exportItemsToAzureDevOps({ workitems });
      if (response.status === 201) {
        alert({
          message: 'Casos de teste exportados para o Azure com sucesso!',
          type: 'success',
          position: 'top-center',
        });
        return true;
      } else {
        alert({
          message: `Erro ao exportar casos de teste para o Azure: ${response.status} | ${response.statusText}`,
          type: 'error',
          position: 'top-center',
        });
        return false;
      }
    } finally {
      this.isExporting = false;
    }

  }
};
